/**
 * SavedMapModal (#557)
 *
 * Shared confirmation shown after a map is saved — whether the user clicked
 * Save or Share, the experience is identical. Confirms the map was saved,
 * surfaces its permalink (already copied to the clipboard), lets the user
 * re-copy it, explains how to use it, and makes clear the link is
 * EasyMap-3-only and meant for sharing between EM3 users. Carries the
 * context-sensitive help icon for saved maps.
 *
 * Rendered through a portal with a high-z overlay so it sits above everything.
 */

import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCopy, faCheck, faShareNodes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import ContextHelp from '../ContextHelp/ContextHelp';
import './SavedMapModal.scss';

interface SavedMapModalProps {
  isOpen: boolean;
  url: string;
  /** Optional saved-map name, shown in the confirmation for clarity. */
  name?: string;
  onClose: () => void;
}

export const SavedMapModal: React.FC<SavedMapModalProps> = ({ isOpen, url, name, onClose }) => {
  const [recopied, setRecopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setRecopied(true);
      setTimeout(() => setRecopied(false), 2000);
    } catch {
      // Clipboard can fail (permissions/non-secure context); the link is still
      // visible in the field for manual selection, so swallow quietly.
    }
  }, [url]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="saved-map-modal-overlay" onClick={onClose}>
      <div
        className="saved-map-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="saved-map-modal-title"
      >
        <div className="saved-map-modal-header">
          <h3 id="saved-map-modal-title">
            <FontAwesomeIcon icon={faCheck} /> Map saved
          </h3>
          <div className="saved-map-modal-header-actions">
            <ContextHelp title="Saved Maps Help" context="saved-map-help" />
            <button className="close-btn" onClick={onClose} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        <div className="saved-map-modal-body">
          <p className="saved-map-lead">
            {name ? <>Your map <strong>“{name}”</strong> has been saved.</> : <>Your map has been saved.</>}
            {' '}A permalink to it has been <strong>copied to your clipboard</strong> — paste it into
            an email or chat to share this exact view with another EasyMap&nbsp;3 user.
          </p>

          <label className="saved-map-field-label" htmlFor="saved-map-url">Permalink</label>
          <div className="saved-map-field">
            <input
              id="saved-map-url"
              type="text"
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="saved-map-copy-btn"
              onClick={handleCopy}
              aria-label="Copy link"
              title="Copy link to clipboard"
            >
              <FontAwesomeIcon icon={recopied ? faCheck : faCopy} />
              <span>{recopied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>

          <h4>What this does</h4>
          <p>
            The permalink restores the map position (centre &amp; zoom), the active layers with
            their order and transparency, and the selected background — exactly as you see them now.
          </p>

          <h4>How to use it</h4>
          <ol className="saved-map-steps">
            <li>The permalink is already on your clipboard — paste it (Ctrl/Cmd&nbsp;+&nbsp;V) into an email or chat.</li>
            <li>Need it again? Use the <strong>Copy</strong> button above, or the link icon next to this map in <strong>Load</strong>.</li>
            <li>When the recipient opens it, EasyMap&nbsp;3 rebuilds this view for them.</li>
          </ol>

          <div className="saved-map-notice">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>
              This link <strong>only works inside EasyMap 3</strong> and is meant for sharing
              between EasyMap&nbsp;3 users. If the recipient is not signed in, they will be sent
              to the login page first and then continue on to the shared map.
            </span>
          </div>
        </div>

        <div className="saved-map-modal-footer">
          <FontAwesomeIcon icon={faShareNodes} className="saved-map-footer-icon" />
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SavedMapModal;
